import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  testCompletionApi,
  type TestCompletionEndpoint,
  type TestCompletionMode,
  type TestCompletionRunDetail,
} from '@/api/testCompletion'
import { coachingKeys } from './useCoachingToday'

export const testCompletionKeys = {
  all: ['test-completion'] as const,
  capability: () => [...testCompletionKeys.all, 'capability'] as const,
  active: () => [...testCompletionKeys.all, 'active'] as const,
  detail: (runId: number) => [...testCompletionKeys.all, 'run', runId] as const,
  history: (learnerId: number) => [...testCompletionKeys.all, 'learner', learnerId, 'history'] as const,
}

const ACTIVE_STATUSES = new Set(['QUEUED', 'RUNNING', 'CANCEL_REQUESTED'])

interface ObservedRun {
  id: number
  learnerId: number
}

function refreshCompletedRunState(queryClient: QueryClient, learnerId: number) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['coaching', 'learner', learnerId] }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.today() }),
    queryClient.invalidateQueries({ queryKey: testCompletionKeys.capability() }),
    queryClient.invalidateQueries({ queryKey: testCompletionKeys.history(learnerId) }),
  ])
}

function observeActiveRun(
  queryClient: QueryClient,
  run: TestCompletionRunDetail,
  learnerId: number,
) {
  // Preserve the mutation response long enough for the active-run observer to record the learner.
  // A short run may already be absent from /runs/active by the time the next request returns.
  queryClient.setQueryData(testCompletionKeys.active(), run)
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: testCompletionKeys.capability() }),
    queryClient.invalidateQueries({ queryKey: testCompletionKeys.history(learnerId) }),
  ])
}

export function useTestCompletionCapability() {
  return useQuery({
    queryKey: testCompletionKeys.capability(),
    queryFn: testCompletionApi.capability,
    staleTime: 30_000,
  })
}

export function useActiveTestCompletion(enabled: boolean) {
  const queryClient = useQueryClient()
  const lastObservedRun = useRef<ObservedRun | null>(null)
  const query = useQuery({
    queryKey: testCompletionKeys.active(),
    queryFn: testCompletionApi.active,
    enabled,
    refetchInterval: (query) => ACTIVE_STATUSES.has(query.state.data?.status ?? '') ? 1_000 : false,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })
  const activeRunId = query.data?.id ?? null
  const activeLearnerId = query.data?.learner.id ?? null

  useEffect(() => {
    const previousRun = lastObservedRun.current
    if (activeRunId != null && activeLearnerId != null) {
      if (previousRun != null && previousRun.id !== activeRunId) {
        void refreshCompletedRunState(queryClient, previousRun.learnerId)
      }
      lastObservedRun.current = { id: activeRunId, learnerId: activeLearnerId }
      return
    }
    if (!query.isSuccess || previousRun == null) return

    lastObservedRun.current = null
    void refreshCompletedRunState(queryClient, previousRun.learnerId)
  }, [activeLearnerId, activeRunId, query.isSuccess, queryClient])

  return query
}

export function useTestCompletionHistory(learnerId: number, enabled = true) {
  return useQuery({
    queryKey: testCompletionKeys.history(learnerId),
    queryFn: () => testCompletionApi.history(learnerId),
    enabled: enabled && learnerId > 0,
  })
}

export function useTestCompletionActions(learnerId: number) {
  const queryClient = useQueryClient()
  const observeRun = (run: TestCompletionRunDetail) =>
    observeActiveRun(queryClient, run, learnerId)

  const preflight = useMutation({
    mutationFn: (request: { completionMode: TestCompletionMode; endpoint: TestCompletionEndpoint }) =>
      testCompletionApi.preflight(learnerId, request),
  })
  const start = useMutation({
    mutationFn: (request: {
      completionMode: TestCompletionMode
      endpoint: TestCompletionEndpoint
      planHash: string
      typedConfirmation?: string
    }) => testCompletionApi.start(learnerId, request),
    onSuccess: observeRun,
  })
  const cancel = useMutation({
    mutationFn: testCompletionApi.cancel,
    onSuccess: observeRun,
  })
  const resumePreflight = useMutation({ mutationFn: testCompletionApi.resumePreflight })
  const resume = useMutation({
    mutationFn: ({ runId, planHash }: { runId: number; planHash: string }) =>
      testCompletionApi.resume(runId, planHash),
    onSuccess: observeRun,
  })

  return { preflight, start, cancel, resumePreflight, resume }
}
